'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface EventFiltersProps {
  selectedCategory: string;
  selectedLocation: string;
  selectedStatus: string;
  onCategoryChange: (category: string) => void;
  onLocationChange: (location: string) => void;
  onStatusChange: (status: string) => void;
}

const EventFilters: React.FC<EventFiltersProps> = ({
  selectedCategory,
  selectedLocation,
  selectedStatus,
  onCategoryChange,
  onLocationChange,
  onStatusChange,
}) => {
  const categories = [
    { value: 'all', label: 'All Categories', icon: 'Squares2X2Icon' },
    { value: 'health', label: 'Health Camp', icon: 'HeartIcon' },
    { value: 'community', label: 'Community Service', icon: 'UserGroupIcon' },
    { value: 'awareness', label: 'Awareness Drive', icon: 'MegaphoneIcon' },
    { value: 'festival', label: 'Festival Celebration', icon: 'SparklesIcon' },
  ];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'sarthana', label: 'Sarthana' },
    { value: 'varachha', label: 'Varachha' },
    { value: 'kadodara', label: 'Kadodara' },
  ];

  const statuses = [
    { value: 'all', label: 'All Events' },
    { value: 'open', label: 'Registration Open' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'past', label: 'Past Events' },
  ];

  return (
    <div className="bg-card rounded-lg p-6 shadow-subtle">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="FunnelIcon" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-bold text-foreground">Filter Events</h3>
      </div>

      <div className="space-y-6 flex flex-col sm:flex-row sm:space-y-0 sm:space-x-6 lg:flex-col lg:space-y-6 lg:space-x-0">
        {/* Category Filter */}
        <div className='flex-1'>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Category
          </label>
          <div className="space-y-1">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => onCategoryChange(category.value)}
                className={`w-full flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all duration-200 ${
                  selectedCategory === category.value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                <Icon name={category.icon as any} size={14} />
                <span className="text-xs font-medium">{category.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Location Filter */}
        <div className='flex-1'>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Location
          </label>
          <div className="space-y-1">
            {locations.map((location) => (
              <button
                key={location.value}
                onClick={() => onLocationChange(location.value)}
                className={`w-full flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all duration-200 ${
                  selectedLocation === location.value
                    ? 'bg-secondary text-secondary-foreground shadow-sm'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                <Icon name="MapPinIcon" size={14} />
                <span className="text-xs font-medium">{location.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Status Filter */}
        <div className='flex-1'>
          <label className="block text-sm font-semibold text-foreground mb-3">
            Status
          </label>
          <div className="space-y-1">
            {statuses.map((status) => (
              <button
                key={status.value}
                onClick={() => onStatusChange(status.value)}
                className={`w-full flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all duration-200 ${
                  selectedStatus === status.value
                    ? 'bg-accent text-accent-foreground shadow-sm'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                <Icon name="CheckCircleIcon" size={14} />
                <span className="text-xs font-medium">{status.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventFilters;